/**
 * Slider module for handling Swiper.js integration.
 * Provides configuration and initialization for various slider types.
 */

import { register } from 'swiper/element/bundle';

const sliderCss = new URL('/theme/assets/slider.css', import.meta.url).href;

register();

/**
 * Base configuration that all sliders inherit from.
 * @type {Object}
 */
const baseConfig = {
  spaceBetween: 16,
  injectStylesUrls: [sliderCss]
};

/**
 * Navigation button styles for sliders.
 * @type {Object}
 */
const navigationStyles = {
  injectStyles: [
    `
      .swiper-button-next svg,
      .swiper-button-prev svg {
        display: none;
      }
      .swiper-button-next,
      .swiper-button-prev {
        background-color: var(--color-white);
        border-radius: 50%;
        width: 2.8rem;
        height: 2.8rem;
        background-image: url("data:image/svg+xml,%3Csvg fill='none' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M7.146 4.646a.5.5 0 0 1 .708 0l5 5a.5.5 0 0 1 0 .708l-5 5a.5.5 0 0 1-.708-.708L11.793 10 7.146 5.354a.5.5 0 0 1 0-.708Z' fill='%23171717'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        background-size: 2rem;
        box-shadow: 0px 4px 16px 0px rgba(23, 23, 23, 0.06);
        will-change: background-color;
        transition: background-color var(--duration-medium) ease-in-out;
      }
      .swiper-button-prev {
        transform: rotate(180deg);
      }
      .swiper-button-next:hover,
      .swiper-button-prev:hover {
        background-color: var(--color-neutral-tint);
      }
      @media (min-width: 768px) {
        .swiper-button-next,
        .swiper-button-prev {
          width: 4rem;
          height: 4rem;
          background-size: 1.6rem;
        }
      }
    `
  ]
};

/**
 * Shared styles for product carousel sliders (related products, YMAL).
 * @type {Array<string>}
 */
const productCarouselInjectStyles = [
  `
        .swiper-pagination {
          margin-top: 2.4rem;
        }

        .swiper-button-prev, .swiper-button-next {
          inset: unset;
          right: 0;
          bottom: 0;
          width: 3.2rem;
          height: 3.2rem;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 50%;
          border: 1px solid var(--color-grey);
          background: var(--color-white);
          color: var(--color-black);
        }

        .swiper-button-prev {
          right: 4rem;
        }

        .swiper-button-prev svg, .swiper-button-next svg {
          width: 1.2rem;
          height: 1.2rem;
        }

        @media (max-width: 767px) {

          .swiper-button-prev, .swiper-button-next {
            display: none
          }
        
        }

        @media (min-width: 768px) {

          .swiper {
            padding-bottom: var(--spacing-4xl);
          }

          .swiper-button-prev, .swiper-button-next {
            inset: unset;
            bottom: 0;
          }

          .swiper-button-next {
            left: 4rem;
          }
   
        }
      `
];

/**
 * Map of slider configurations keyed by their class names.
 * @type {Object}
 */
const sliderConfigs = {
  default: {
    ...baseConfig
  },
  'js-related-products': {
    ...baseConfig,
    spaceBetween: 8,
    slidesPerView: 1.2,
    pagination: false,
    navigation: true,
    breakpoints: {
      768: {
        slidesPerView: 2.2
      },
      1024: {
        slidesPerView: 3.2
      }
    },
    injectStyles: productCarouselInjectStyles
  },
  'js-ymal-carousel': {
    ...baseConfig,
    spaceBetween: 8,
    slidesPerView: 2,
    pagination: false,
    navigation: true,
    watchOverflow: true,
    keyboard: {
      enabled: true,
      onlyInViewport: true
    },
    breakpoints: {
      768: {
        slidesPerView: 3
      },
      1024: {
        slidesPerView: 4
      }
    },
    injectStyles: [
      ...productCarouselInjectStyles,
      `
        .swiper-button-lock {
          display: none;
        }
      `
    ]
  },
  'js-product-media-gallery-slider': {
    ...baseConfig,
    ...navigationStyles,
    enabled: window.innerWidth <= 768,
    slidesPerView: 1,
    loop: true,
    loopedSlides: 20,
    watchSlidesProgress: true,
    slideToClickedSlide: false,
    navigation: true,
    preloadImages: true,
    updateOnImagesReady: true
  },
  'js-product-media-gallery-slider-thumbnails': {
    ...baseConfig,
    enabled: window.innerWidth <= 768,
    slidesPerView: 6,
    centeredSlides: true,
    loop: true,
    loopedSlides: 20,
    spaceBetween: 4,
    watchSlidesProgress: true,
    slideToClickedSlide: false,
    preloadImages: true,
    updateOnImagesReady: true,
    breakpoints: {
      768: {
        spaceBetween: 16
      }
    }
  },
  'js-product-brand-carousel': {
    ...baseConfig,
    slidesPerView: 1,
    pagination: {
      clickable: true
    },
    injectStyles: [
      `
        .swiper-pagination {
          margin-top: var(--spacing-lg);
        }
        .swiper-wrapper {
          align-items: flex-end;
        }
      `
    ]
  },
  'js-cart-drawer-collection-slider': {
    ...baseConfig,
    direction: 'vertical',
    slidesPerView: 'auto',
    spaceBetween: 32,
    mousewheel: true,
    injectStyles: [
      `
        .cart-drawer__collection-slider {
          height: 100%;
          max-height: 40rem;
          min-height: 20rem;
        }
        .cart-drawer__collection-slide {
          height: auto;
          width: 100%;
          flex-shrink: 0;
        }
        .cart-drawer__collection-product {
          width: 100%;
        }
        .cart-drawer__collection-slider .swiper-wrapper {
          height: auto;
        }
      `
    ]
  },
  'js-cart-drawer-collection-slider-horizontal': {
    ...baseConfig,
    slidesPerView: 2.1,
    spaceBetween: 16,
    watchSlidesProgress: true,
    updateOnImagesReady: true,
    injectStyles: [
      `
        .cart-drawer__collection-slider-horizontal {
          width: 100%;
          overflow: hidden;
        }
      `
    ]
  }
};

/**
 * Pauses all videos in a slider (HTML5, YouTube, and Vimeo).
 * @param {Object} options - Pause all videos options.
 * @param {Element} options.slider - The slider element.
 */
const pauseAllVideos = ({ slider }) => {
  // Pause all HTML5 videos in the slider.
  const videos = slider.querySelectorAll('video');
  Array.prototype.forEach.call(videos, (video) => {
    if (!video.paused) {
      video.pause();
    }
  });

  // Pause YouTube videos.
  const youtubeIframes = slider.querySelectorAll('iframe[src*="youtube.com"], .js-youtube');
  youtubeIframes.forEach((iframe) => {
    try {
      if (iframe.contentWindow && iframe.contentWindow.postMessage) {
        iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      }
    } catch {

      // console.warn("Could not pause YouTube video");
    }
  });

  // Pause Vimeo videos.
  const vimeoIframes = slider.querySelectorAll('iframe[src*="vimeo.com"], .js-vimeo');
  vimeoIframes.forEach((iframe) => {
    try {
      if (iframe.contentWindow && iframe.contentWindow.postMessage) {
        iframe.contentWindow.postMessage('{"method":"pause"}', '*');
      }
    } catch {

      // console.warn("Could not pause Vimeo video");
    }
  });
};

/**
 * Handles video playback for a slider - pauses all videos and plays the active one.
 * @param {Object} options - Handle video playback options.
 * @param {Element} options.slider - The slider element.
 */
const handleVideoPlayback = ({ slider }) => {
  // Pause all videos in the slider first.
  pauseAllVideos({ slider });

  // Get the active slide and play its video.
  const activeIndex = slider.swiper ? slider.swiper.realIndex : 0;
  const slides = slider.querySelectorAll('swiper-slide');

  // Validate activeIndex to prevent object injection.
  if (typeof activeIndex !== 'number' || activeIndex < 0 || activeIndex >= slides.length) {
    return;
  }

  let activeSlide = null;
  if (slides && Array.isArray(slides) && activeIndex >= 0 && activeIndex < slides.length) {
    activeSlide = slides[`${activeIndex}`];
  }

  if (!activeSlide) {
    return;
  }

  // Look for video in the active slide.
  const activeSlideVideo = activeSlide.querySelector('video');
  if (activeSlideVideo && activeSlideVideo.paused) {
    // Set video to loop and mute for autoplay compatibility.
    activeSlideVideo.loop = true;
    activeSlideVideo.muted = true;

    // Add event listener to restart video if it ends (fallback for some browsers).
    activeSlideVideo.addEventListener('ended', () => {
      activeSlideVideo.currentTime = 0;
      activeSlideVideo.play().catch(() => {

        // console.warn("Could not restart video:", error);
      });
    });

    activeSlideVideo.play().catch(() => {

      // console.warn("Could not autoplay video:", error);
    });
  }

  // Look for YouTube iframe in the active slide.
  const activeSlideYouTube = activeSlide.querySelector('iframe[src*="youtube.com"], .js-youtube');
  if (activeSlideYouTube) {
    try {
      if (activeSlideYouTube?.contentWindow?.postMessage) {
        // Add loop and mute parameters for YouTube videos.
        const currentSrc = activeSlideYouTube.src;
        if (!currentSrc.includes('loop=1')) {
          const separator = currentSrc.includes('?') ? '&' : '?';
          const videoId = currentSrc.match(/[?&]v=([^&]+)/)?.[1] || '';
          activeSlideYouTube.src = `${currentSrc}${separator}loop=1&mute=1&playlist=${videoId}`;
        } else if (!currentSrc.includes('mute=1')) {
          const separator = currentSrc.includes('?') ? '&' : '?';
          activeSlideYouTube.src = `${currentSrc}${separator}mute=1`;
        }
        activeSlideYouTube.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      }
    } catch {

      // console.warn('Could not play YouTube video');
    }
  }

  // Look for Vimeo iframe in the active slide.
  const activeSlideVimeo = activeSlide.querySelector('iframe[src*="vimeo.com"], .js-vimeo');
  if (activeSlideVimeo) {
    try {
      if (activeSlideVimeo?.contentWindow?.postMessage) {
        // Add loop and mute parameters for Vimeo videos.
        const currentSrc = activeSlideVimeo.src;
        if (!currentSrc.includes('loop=1')) {
          const separator = currentSrc.includes('?') ? '&' : '?';
          activeSlideVimeo.src = `${currentSrc}${separator}loop=1&muted=1`;
        } else if (!currentSrc.includes('muted=1')) {
          const separator = currentSrc.includes('?') ? '&' : '?';
          activeSlideVimeo.src = `${currentSrc}${separator}muted=1`;
        }
        activeSlideVimeo.contentWindow.postMessage('{"method":"play"}', '*');
      }
    } catch {

      // console.warn('Could not play Vimeo video');
    }
  }
};

// Create Theme namespace to store slider instances and configurations globally.
// @TODO: We should organise all these globals into a single object.
window.Theme = window.Theme || {};
window.Theme.sliders = { instances: [] };

// Track current window width for resize detection.
let resizeTimeout = null;

/**
 * Debounced resize handler to prevent excessive reinitialization.
 * @param {Object} options - Debounce options.
 * @param {Function} options.callback - The function to call after debounce delay.
 * @param {number} options.delay - The delay in milliseconds.
 * @returns {Function} - The debounced function.
 */
const debounce = ({ callback, delay }) => {
  return (...args) => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    resizeTimeout = setTimeout(() => callback(...args), delay);
  };
};

/**
 * Handles window resize events to enable/disable product gallery sliders.
 */
const handleResize = () => {
  const mainSlider = document.querySelector('.js-product-media-gallery-slider');
  const thumbsSlider = document.querySelector('.js-product-media-gallery-slider-thumbnails');

  if (!mainSlider || !thumbsSlider) {
    return;
  }

  const mainSwiper = mainSlider.swiper;
  const thumbsSwiper = thumbsSlider.swiper;

  if (!mainSwiper || !thumbsSwiper) {
    return;
  }

  const isMobile = window.innerWidth <= 768;

  if (isMobile) {
    // Enable sliders if they are disabled on mobile viewports.
    if (!mainSwiper.enabled) {
      mainSwiper.enable();
      mainSwiper.update();
      // Add initialized class to main slider when enabled.
      mainSlider.classList.add('js-product-media-gallery-slider--initialised');
    }
    if (!thumbsSwiper.enabled) {
      thumbsSwiper.enable();
      // Force Swiper to rebuild the loop slides, which is necessary when enabling a looped slider.
      thumbsSwiper.loopCreate();
      thumbsSwiper.update();
      // Resync the thumbnail slider to the main one without animation.
      thumbsSwiper.slideToLoop(mainSwiper.realIndex, 0);
      // Add initialized class to thumbnails slider when enabled.
      thumbsSlider.classList.add('js-product-media-gallery-slider-thumbnails--initialised');
    }
  } else {
    // Disable sliders if they are enabled on desktop viewports.
    if (mainSwiper.enabled) {
      mainSwiper.disable();
      // Pause all videos when transitioning to desktop.
      pauseAllVideos({ slider: mainSlider });
    }
    if (thumbsSwiper.enabled) {
      thumbsSwiper.disable();
    }
  }
};

/**
 * Initialises sliders with their respective configurations.
 * @param {Document|Element} [root] - Scope to initialise sliders within.
 *   Defaults to the document; the theme editor passes the reloaded section.
 */
const initSliders = (root = document) => {
  const swiperEls = root.querySelectorAll('swiper-container');

  swiperEls.forEach((slider) => {
    // Skip sliders that are already initialised (e.g. when the theme editor
    // reloads one section and we re-scan a broader root).
    if (slider.swiper) {
      return;
    }

    // Find the matching configuration or use default.
    const configKey =
      Array.from(slider.classList).find((className) =>
        Object.prototype.hasOwnProperty.call(sliderConfigs, className)
      ) || 'default';

    // Apply configuration and initialize.
    let config;
    switch (configKey) {
      case 'js-related-products':
        config = sliderConfigs['js-related-products'];
        break;
      case 'js-ymal-carousel':
        config = sliderConfigs['js-ymal-carousel'];
        break;
      case 'js-product-media-gallery-slider':
        config = sliderConfigs['js-product-media-gallery-slider'];
        break;
      case 'js-product-media-gallery-slider-thumbnails':
        config = sliderConfigs['js-product-media-gallery-slider-thumbnails'];
        break;
      case 'js-product-brand-carousel':
        config = sliderConfigs['js-product-brand-carousel'];
        break;
      case 'js-cart-drawer-collection-slider':
        config = sliderConfigs['js-cart-drawer-collection-slider'];
        break;
      case 'js-cart-drawer-collection-slider-horizontal':
        config = sliderConfigs['js-cart-drawer-collection-slider-horizontal'];
        break;
      default:
        config = sliderConfigs.default;
    }

    Object.assign(slider, config);
    slider.initialize();

    // Add initialised class for brand carousel.
    if (configKey === 'js-product-brand-carousel') {
      slider.classList.add('js-product-brand-carousel--initialised');
    }

    // Add initialised class for announcement bar slider.
    if (slider.classList.contains('js-announcement-bar-slider')) {
      slider.classList.add('announcement-bar__slider--initialised');
    }

    // Store the instance with an identifier to be accessed later.
    window.Theme.sliders.instances.push({
      id: slider.classList[0],
      element: slider,
      type: configKey,
      config
    });

    // Add video autoplay functionality after slider is initialized.
    setTimeout(() => {
      // Add event listener for slide changes.
      slider.addEventListener('swiperslidechange', () => {
        handleVideoPlayback({ slider });
      });

      // Fallback: also try to add event listener to the swiper instance.
      if (slider.swiper) {
        slider.swiper.on('slideChange', () => {
          handleVideoPlayback({ slider });
        });
      }

      // Handle initial video autoplay for the first slide.
      if (configKey === 'js-product-media-gallery-slider') {
        handleVideoPlayback({ slider });
      }
    }, 500);
  });

  // Link main gallery and thumbnail sliders after initialization.
  const mainSlider = root.querySelector('.js-product-media-gallery-slider');
  const thumbsSlider = root.querySelector('.js-product-media-gallery-slider-thumbnails');

  // Defensive checks to ensure both sliders exist and are initialized.
  if (!(mainSlider && thumbsSlider)) return;
  // Add a small delay to ensure Swiper instances are fully initialized.
  setTimeout(() => {
    if (!(mainSlider.swiper && thumbsSlider.swiper)) return;
    // Add initialized class to main slider.
    mainSlider.classList.add('js-product-media-gallery-slider--initialised');
    // Add initialized class to thumbnails slider.
    thumbsSlider.classList.add('js-product-media-gallery-slider-thumbnails--initialised');

    // Keep thumbnails centered on main slider change.
    mainSlider.addEventListener('swiperslidechange', (event) => {
      const mainSwiperInstance = event.detail[0];
      if (mainSwiperInstance && thumbsSlider.swiper) {
        const activeIndex = mainSwiperInstance.realIndex;
        thumbsSlider.swiper.slideToLoop(activeIndex);

        // Update active states.
        const thumbnails = thumbsSlider.querySelectorAll('swiper-slide');
        thumbnails.forEach((slide) => {
          slide.classList.remove('is-active');
        });
        const activeThumbnails = thumbsSlider.querySelectorAll(
          `swiper-slide[data-swiper-slide-index="${activeIndex}"]`
        );
        activeThumbnails.forEach((slide) => {
          slide.classList.add('is-active');
        });
      }
    });

    // Add click handling for thumbnails.
    thumbsSlider.addEventListener('click', (event) => {
      const clickedSlide = event.target.closest('swiper-slide');
      if (!clickedSlide || !clickedSlide.swiper || !clickedSlide.dataset.swiperSlideIndex) {
        return;
      }
      const clickedIndex = parseInt(clickedSlide.dataset.swiperSlideIndex, 10);
      mainSlider.swiper.slideToLoop(clickedIndex);
    });

    // Set initial state.
    const initialIndex = mainSlider.swiper.realIndex;
    thumbsSlider.swiper.slideToLoop(initialIndex);
    const initialThumbnail = thumbsSlider.querySelector(`swiper-slide:nth-child(${initialIndex + 1})`);
    if (initialThumbnail) {
      initialThumbnail.classList.add('is-active');
    }

    // Force update both sliders.
    mainSlider.swiper.update();
    thumbsSlider.swiper.update();
  }, 100);
};

// Initialise sliders inside sections the theme editor injects or re-renders;
// these never receive DOMContentLoaded, so without this uninitialised
// swiper-containers render an empty shadow root (no visible slides).
document.addEventListener('shopify:section:load', (event) => initSliders(event.target));

document.addEventListener('DOMContentLoaded', () => {
  initSliders();

  // Add resize event listener with debouncing.
  const debouncedResizeHandler = debounce({
    callback: handleResize,
    delay: 250
  });
  window.addEventListener('resize', debouncedResizeHandler);

  // Clean up event listener on page unload.
  window.addEventListener('beforeunload', () => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    window.removeEventListener('resize', debouncedResizeHandler);
  });
});
